import rawSiteIndex from './generated/site-index.json';
import { resolveRuntimeRoute } from './runtime-routes';
import type { SearchRecord, SiteIndex, SiteLanguage, SitePage, SitePageSummary } from './types';

export const siteIndex = rawSiteIndex as unknown as SiteIndex;
export const pages = siteIndex.pages;
export const locales = siteIndex.locales;
export const collections = siteIndex.collections ?? [];
export const readerPaths = siteIndex.readerPaths ?? [];
export const featuredQuestions = siteIndex.featuredQuestions ?? [];
export const pagesById = new Map(pages.map((page) => [page.id, page]));
export const localesByCode = new Map(locales.map((locale) => [locale.code, locale]));

export function normaliseRoute(value: string): string {
  const pathOnly = value.split(/[?#]/, 1)[0];
  let decoded = pathOnly;
  try { decoded = decodeURIComponent(pathOnly); } catch { /* preserve malformed literal */ }
  const cleaned = decoded.normalize('NFC').replace(/\/{2,}/g, '/').replace(/\/$/, '');
  return cleaned || '/';
}

const routeToPage = new Map<string, SitePageSummary>();
for (const page of pages) {
  // Generated route and alias keys are already normalized by the knowledge loader.
  // Re-normalizing them would reinterpret encoded reserved delimiters.
  for (const route of [page.route, ...page.aliases]) {
    routeToPage.set(route, page);
    if (/%[0-9a-f]{2}/i.test(route)) routeToPage.set(normaliseRoute(route), page);
  }
}

const generatedPageModules = import.meta.glob<{ default: SitePage }>('./generated/pages/*.json');
const loaderById = new Map<string, () => Promise<{ default: SitePage }>>();
for (const [modulePath, loader] of Object.entries(generatedPageModules)) {
  const id = modulePath.split('/').at(-1)?.replace(/\.json$/, '');
  if (id) loaderById.set(id, loader);
}
const pageCache = new Map<string, Promise<SitePage>>();
let searchIndexPromise: Promise<SearchRecord[]> | null = null;

export function pageForRoute(route: string): SitePageSummary | null {
  const normalised = normaliseRoute(route);
  return routeToPage.get(normalised) ?? routeToPage.get(resolveRuntimeRoute(normalised)) ?? null;
}

export async function loadPageById(id: string): Promise<SitePage> {
  const existing = pageCache.get(id);
  if (existing) return existing;
  const loader = loaderById.get(id);
  if (!loader) throw new Error(`No generated content module for page: ${id}`);
  const pending = loader().then((module) => module.default);
  pageCache.set(id, pending);
  return pending;
}

export async function loadPageForRoute(route: string): Promise<SitePage | null> {
  const summary = pageForRoute(route);
  return summary ? loadPageById(summary.id) : null;
}

export function loadSearchIndex(): Promise<SearchRecord[]> {
  if (!searchIndexPromise) {
    searchIndexPromise = import('./generated/search-index.json').then((module) => module.default as SearchRecord[]);
  }
  return searchIndexPromise;
}

export function homeForLanguage(language: SiteLanguage): SitePageSummary {
  return pages.find((page) => page.lang === language && page.kind === 'home')
    ?? pagesById.get('home-en')!;
}

export function pagesForLanguage(language: SiteLanguage): SitePageSummary[] {
  return pages.filter((page) => page.lang === language);
}

export function pageForKnowledgeId(language: SiteLanguage, knowledgeId: string): SitePageSummary | null {
  return pages.find((page) => page.lang === language && page.knowledgeId === knowledgeId) ?? null;
}

export function pagesForCollection(language: SiteLanguage, collection: string): SitePageSummary[] {
  return pages.filter((page) => page.lang === language && page.collection === collection && !['collection', 'home', 'explorer', 'comparison'].includes(page.kind));
}

export function pageForTranslationKey(language: SiteLanguage, translationKey: string): SitePageSummary | null {
  return pages.find((page) => page.lang === language && page.translationKey === translationKey) ?? null;
}

export function translationsFor(page: SitePageSummary): SitePageSummary[] {
  return pages.filter((candidate) => candidate.translationKey === page.translationKey);
}

export function translationFor(page: SitePageSummary, language?: SiteLanguage): SitePageSummary | null {
  if (language) return pageForTranslationKey(language, page.translationKey);
  if (page.translationId) return pagesById.get(page.translationId) ?? null;
  return pageForTranslationKey(page.lang === 'en' ? 'es' : 'en', page.translationKey);
}

export function canonicalRouteFor(page: SitePageSummary): string {
  return page.kind === 'home' && page.lang === 'en' ? '/' : page.route;
}
